import styled, { useTheme } from 'styled-components'
import { FieldDetail } from '../../components/basic/FieldDetail'
import { TagDetails } from '../../components/tags/TagDetails'
import { StarredCell } from '../../components/starred/StarredCell'
import { ICON_NAMES } from '../../common/src/msIcons'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { ActionButton, DirectionalHint, Icon, IconButton } from '@fluentui/react'
import { TooltipHost } from '@/components/tooltip/TooltipHost'
import { SERVER_URLS } from '../../common/src/constants'
import { openExternalLink } from '../../utils/externalLinkHandler'
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from '@fluentui/react-components'

export const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: visible;
`

export const SectionsContainer = styled.div``

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 16px;
`

export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin-top: 14px;
  margin-bottom: 4px;

  & .menu-button {
    transition: opacity 0.2s ease;
    opacity: ${p => (p.narrow ? 1 : 0)};
  }
  @media (hover: hover) and (pointer: fine) {
    &:hover .menu-button {
      opacity: 1;
    }
  }
`

export const QuadrantContainer = styled.div``

const getProjectNameStyles = theme => ({
  tooltipHost: {
    root: {
      minWidth: 0,
      display: 'flex',
    },
  },
  actionButton: {
    root: {
      height: 'auto',
      padding: 0,
      minWidth: 0,
    },
    label: {
      margin: 0,
      lineHeight: 'inherit',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      fontWeight: 300,
      fontSize: 14,
    },
    labelDisabled: {
      color: theme.palette.black,
    },
    textContainer: {
      overflow: 'hidden',
    },
  },
})

const moveProjectButtonStyles = {
  tooltipHost: {
    root: { marginLeft: 2 },
  },
  iconButton: {
    icon: {
      paddingTop: 4,
    },
  },
}

export const ProjectName = ({ text, onClick, ...rest }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const projectNameStyles = useMemo(() => getProjectNameStyles(theme), [theme])
  return (
    <TooltipHost content={onClick && t('item_detail.project_detail')} styles={projectNameStyles.tooltipHost}>
      <ActionButton
        text={text}
        disabled={!onClick}
        onClick={onClick}
        styles={projectNameStyles.actionButton}
        {...rest}
      />
    </TooltipHost>
  )
}

export const MoveProjectButton = ({ onClick }) => {
  const { t } = useTranslation()
  return (
    <TooltipHost
      content={t('item_detail.move_to_project_action')}
      styles={moveProjectButtonStyles.tooltipHost}
      directionalHint={DirectionalHint.bottomCenter}
    >
      <IconButton
        className="menu-button"
        iconProps={{ iconName: ICON_NAMES.FabricMoveToFolder }}
        onClick={onClick}
        styles={moveProjectButtonStyles.iconButton}
      />
    </TooltipHost>
  )
}

export const ProjectNameContainer = styled.div`
  display: flex;
  margin-left: 8px;
  flex: 1;
  min-width: 10px;
  align-items: center;
`

export const CompletionDetailContainer = styled(FieldDetail).attrs({
  align: 'center',
})``

export const EffortDetailContainer = styled(FieldDetail).attrs({
  align: 'center',
})``

export const ReminderDetailContainer = styled(FieldDetail).attrs({
  align: 'center',
})``

export const StyledStarredCell = styled(StarredCell)`
  margin: 0 auto;
  display: block;
  font-size: 24px;
`

export const LinkContainer = styled(FieldDetail).attrs({
  align: 'flex-start',
})`
  overflow: hidden;
`

export const DetailRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  flex-wrap: wrap;
  gap: 10px 16px;
`

export const DateSectionContainer = styled(FieldDetail).attrs({
  contentStyle: { alignItems: 'flex-start' },
})``

export const PeopleSectionContainer = styled(FieldDetail)``

export const TagsContainer = styled(FieldDetail).attrs({})``

export const TagDetailsStyled = styled(TagDetails)``

export const InboxIcon = styled(Icon).attrs({
  iconName: ICON_NAMES.Inbox,
})`
  font-size: 30px;
  color: ${p => p.theme.palette.themePrimary};
`

const onClickFactory = href => () => openExternalLink(href) // Duplicated from accounts.js...

export const VersionControlModal = ({ isOpen, onDismiss, itemID }) => {
  const { t } = useTranslation()

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(event, data) => {
        if (!data.open) {
          onDismiss()
        }
      }}
    >
      <DialogSurface className="!max-w-4xl">
        <DialogBody>
          <DialogTitle>{t('item_detail.version_control_action')}</DialogTitle>
          <DialogContent>
            <iframe
              className="h-[80vh] w-full border-none"
              title={t('item_detail.version_control_action')}
              src={SERVER_URLS.VERSION_CONTROL + itemID}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                onClickFactory(SERVER_URLS.VERSION_CONTROL + itemID)()
                onDismiss()
              }}
            >
              {t('item_detail.new_tab_item')}
            </Button>
            <DialogTrigger disableButtonEnhancement>
              <Button>{t('general.close')}</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
